import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Mail } from 'lucide-react';
import LogoIcon from '../components/LogoIcon';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import './Auth.css';

export default function Login() {
  const [method, setMethod] = useState<'password' | 'code' | 'forgot'>('password');
  const [step, setStep] = useState<'email' | 'code' | 'totp'>('email');
  const [mfaMethod, setMfaMethod] = useState<string>('totp');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithCode, loginWithTotp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.mfa_required) {
        setMfaMethod(result.mfa_method || 'totp');
        setStep('totp');
        setMessage(result.mfa_method === 'email'
          ? 'Ein Sicherheitscode wurde an Ihre E-Mail gesendet.'
          : 'Bitte geben Sie den Code aus Ihrer Authenticator-App ein.');
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await loginWithTotp(email, password, totpCode);
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.detail || '2FA-Code ungültig');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await authApi.requestCode(email);
      setStep('code');
      setMessage('Ein 6-stelliger Code wurde an Ihre E-Mail gesendet.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Code konnte nicht gesendet werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await loginWithCode(email, code);
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await authApi.requestCode(email);
      setMessage('Neuer Code wurde gesendet.');
      setCode('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Code konnte nicht gesendet werden.');
    } finally {
      setLoading(false);
    }
  };

  // --- Passwort vergessen ---
  const handleForgotRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      setStep('code');
      setMessage('Ein 6-stelliger Reset-Code wurde an Ihre E-Mail gesendet.');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Senden.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }
    setLoading(true);
    try {
      await authApi.resetPassword(code, newPassword);
      setMessage('Passwort wurde erfolgreich zurückgesetzt. Sie können sich jetzt anmelden.');
      setMethod('password');
      setStep('email');
      setCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Zurücksetzen fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendResetCode = async () => {
    setError('');
    setMessage('');
    setLoading(true);
    try {
      await authApi.forgotPassword(email);
      setMessage('Neuer Reset-Code wurde gesendet.');
      setCode('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Code konnte nicht gesendet werden.');
    } finally {
      setLoading(false);
    }
  };

  const switchMethod = (m: 'password' | 'code' | 'forgot') => {
    setMethod(m);
    setStep('email');
    setError('');
    setMessage('');
    setCode('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <LogoIcon size={40} />
          <h1>CoachDesk365</h1>
          <p className="text-muted">
            {method === 'forgot' ? 'Passwort zurücksetzen' : 'Melden Sie sich in Ihrem Konto an'}
          </p>
        </div>

        {error && <div className="auth-error">{error}</div>}
        {message && <div style={{ background: '#f0fdf4', color: '#166534', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' }}>{message}</div>}

        {/* Passwort-Login */}
        {method === 'password' && step !== 'totp' && (
          <form onSubmit={handlePasswordLogin} autoComplete="on">
            <div className="form-group">
              <label htmlFor="login-email">E-Mail</label>
              <input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ihre@email.de" autoComplete="email" required autoFocus />
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <label htmlFor="login-password">Passwort</label>
                <button type="button" onClick={() => switchMethod('forgot')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '13px', padding: 0 }}>
                  Passwort vergessen?
                </button>
              </div>
              <input id="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Ihr Passwort" autoComplete="current-password" required />
            </div>
            <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
              {loading ? 'Wird angemeldet...' : 'Anmelden'}
            </button>
          </form>
        )}

        {/* 2FA Step (TOTP or Email) */}
        {method === 'password' && step === 'totp' && (
          <form onSubmit={handleTotpLogin} autoComplete="off">
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              {mfaMethod === 'email'
                ? <Mail size={40} style={{ color: 'var(--primary)' }} />
                : <ShieldCheck size={40} style={{ color: 'var(--primary)' }} />
              }
            </div>
            <div className="form-group">
              <label htmlFor="totp-code">
                {mfaMethod === 'email' ? 'E-Mail-Code' : 'Authenticator-Code'}
              </label>
              <input id="totp-code" type="text" inputMode="numeric" maxLength={8} value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))} onKeyDown={(e) => { if (e.key === 'Enter' && totpCode.length >= 6 && !loading) { e.preventDefault(); handleTotpLogin(e as any); } }} placeholder="6-stelliger Code" autoComplete="one-time-code" required autoFocus style={{ fontSize: '24px', letterSpacing: '6px', textAlign: 'center' }} />
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: 4 }}>
                {mfaMethod === 'email'
                  ? 'Prüfen Sie Ihren Posteingang'
                  : 'Code aus Google Authenticator, Authy o.ae.'}
              </p>
            </div>
            <button type="submit" className="btn btn-primary auth-submit" disabled={loading || totpCode.length < 6}>
              {loading ? 'Wird geprüft...' : 'Anmelden'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              {mfaMethod === 'email' && (
                <>
                  <button type="button" onClick={async () => {
                    try { await authApi.mfaResendCode(email, password); setMessage('Neuer Code wurde gesendet.'); setTotpCode(''); }
                    catch { setError('Code konnte nicht gesendet werden.'); }
                  }} disabled={loading} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '14px' }}>Code erneut senden</button>
                  <span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span>
                </>
              )}
              <button type="button" onClick={() => { setStep('email'); setTotpCode(''); setError(''); setMessage(''); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '14px' }}>Zurück</button>
            </div>
          </form>
        )}

        {/* Code-Login */}
        {method === 'code' && step === 'email' && (
          <form onSubmit={handleRequestCode} autoComplete="on">
            <div className="form-group">
              <label htmlFor="login-email">E-Mail</label>
              <input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ihre@email.de" autoComplete="email" required autoFocus />
            </div>
            <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
              {loading ? 'Wird gesendet...' : 'Code senden'}
            </button>
          </form>
        )}
        {method === 'code' && step === 'code' && (
          <form onSubmit={handleVerifyCode} autoComplete="off">
            <div className="form-group">
              <label htmlFor="login-code">Anmeldecode</label>
              <input id="login-code" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6 && !loading) { e.preventDefault(); handleVerifyCode(e as any); } }} placeholder="6-stelliger Code" autoComplete="one-time-code" required autoFocus style={{ fontSize: '24px', letterSpacing: '8px', textAlign: 'center' }} />
            </div>
            <button type="submit" className="btn btn-primary auth-submit" disabled={loading || code.length !== 6}>
              {loading ? 'Wird geprüft...' : 'Anmelden'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button type="button" onClick={handleResendCode} disabled={loading} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '14px' }}>Code erneut senden</button>
              <span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span>
              <button type="button" onClick={() => { setStep('email'); setCode(''); setError(''); setMessage(''); }} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '14px' }}>Andere E-Mail</button>
            </div>
          </form>
        )}

        {/* Passwort vergessen */}
        {method === 'forgot' && step === 'email' && (
          <form onSubmit={handleForgotRequest} autoComplete="on">
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '16px' }}>
              Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen 6-stelligen Code zum Zurücksetzen.
            </p>
            <div className="form-group">
              <label htmlFor="reset-email">E-Mail</label>
              <input id="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ihre@email.de" autoComplete="email" required autoFocus />
            </div>
            <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
              {loading ? 'Wird gesendet...' : 'Reset-Code senden'}
            </button>
          </form>
        )}
        {method === 'forgot' && step === 'code' && (
          <form onSubmit={handleResetPassword} autoComplete="off">
            <div className="form-group">
              <label htmlFor="reset-code">Reset-Code</label>
              <input id="reset-code" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6 && !loading) { e.preventDefault(); handleResetPassword(e as any); } }} placeholder="6-stelliger Code" autoComplete="one-time-code" required autoFocus style={{ fontSize: '24px', letterSpacing: '8px', textAlign: 'center' }} />
            </div>
            <div className="form-group">
              <label htmlFor="new-password">Neues Passwort</label>
              <input id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Mindestens 8 Zeichen" autoComplete="new-password" required minLength={8} />
            </div>
            <div className="form-group">
              <label htmlFor="confirm-password">Passwort bestätigen</label>
              <input id="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Passwort wiederholen" autoComplete="new-password" required minLength={8} />
            </div>
            <button type="submit" className="btn btn-primary auth-submit" disabled={loading || code.length !== 6}>
              {loading ? 'Wird zurückgesetzt...' : 'Passwort zurücksetzen'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button type="button" onClick={handleResendResetCode} disabled={loading} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '14px' }}>Code erneut senden</button>
              <span style={{ margin: '0 8px', color: '#cbd5e1' }}>|</span>
              <button type="button" onClick={() => switchMethod('password')} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '14px' }}>Zurück zum Login</button>
            </div>
          </form>
        )}

        {method !== 'forgot' && (
          <div style={{ textAlign: 'center', marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
            <button type="button" onClick={() => switchMethod(method === 'password' ? 'code' : 'password')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '14px' }}>
              {method === 'password' ? 'Stattdessen mit E-Mail-Code anmelden' : 'Stattdessen mit Passwort anmelden'}
            </button>
          </div>
        )}
        {method === 'forgot' && (
          <div style={{ textAlign: 'center', marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
            <button type="button" onClick={() => switchMethod('password')} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '14px' }}>
              Zurück zur Anmeldung
            </button>
          </div>
        )}

        <p className="auth-footer">
          Noch kein Konto? <Link to={redirectTo !== '/dashboard' ? `/register?redirect=${encodeURIComponent(redirectTo)}` : '/register'}>Jetzt registrieren</Link>
        </p>
        <p className="auth-footer" style={{ marginTop: 4 }}>
          <Link to="/" style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Zurück zur Startseite</Link>
        </p>
      </div>
    </div>
  );
}
